/**
 * FileBrowser Component
 *
 * File browser with grid/list views, folder navigation, and file management.
 */

import { useState, useCallback, useEffect, Fragment, useRef } from 'react';
import JSZip from 'jszip';
import {
  File,
  Folder,
  FolderPlus,
  FolderInput,
  Grid,
  List,
  Search,
  MoreVertical,
  Download,
  Trash2,
  Archive,
  ArchiveRestore,
  ChevronRight,
  Home,
  Loader2,
  FileText,
  Image,
  FileVideo,
  FileAudio,
  FileSpreadsheet,
  X,
  Pencil,
  Expand,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useFiles } from '@/lib/hooks/useFiles';
import { useFolders } from '@/lib/hooks/useFolders';
import { api, FileCategory, FileStatus, UserFileResponse, FolderResponse } from '@/lib/api';
import { FilePreview } from './FilePreview';

/** Protected folder paths that cannot be deleted or renamed */
const PROTECTED_FOLDER_PATHS = ['/My-Fias', '/My-Fias/Context'];

/** My-Fias folder path - users cannot modify folder structure within */
const MY_FIAS_PATH = '/My-Fias';

/**
 * Check if a folder path is protected (cannot be deleted or renamed)
 */
function isProtectedFolder(path: string): boolean {
  return PROTECTED_FOLDER_PATHS.includes(path);
}

/**
 * Check if a folder is within the My-Fias structure (cannot create subfolders)
 */
function isWithinMyFias(path: string | null | undefined): boolean {
  if (!path) return false;
  return path === MY_FIAS_PATH || path.startsWith(MY_FIAS_PATH + '/');
}

export interface FileBrowserProps {
  /** Initial folder to display */
  initialFolderId?: string;
  /** Callback when file is selected */
  onFileSelect?: (file: UserFileResponse) => void;
  /** Callback when file is double-clicked (for preview) */
  onFileOpen?: (file: UserFileResponse) => void;
  /** Callback when files are deleted */
  onFilesDeleted?: (fileIds: string[]) => void;
  /** Callback when file is archived */
  onFileArchived?: (fileId: string) => void;
  /** Callback when file is restored */
  onFileRestored?: (fileId: string) => void;
  /** Enable multi-select mode */
  multiSelect?: boolean;
  /** Filter by category */
  category?: FileCategory;
  /** Filter by status */
  status?: FileStatus;
  /** Additional CSS classes */
  className?: string;
  /** Trigger to refresh file list (increment to refresh) */
  refreshTrigger?: number;
}

type ViewMode = 'grid' | 'list';
type SortBy = 'name' | 'created' | 'size';
type SortOrder = 'asc' | 'desc';

/**
 * File browser component
 */
export function FileBrowser({
  initialFolderId,
  onFileSelect,
  onFileOpen,
  onFilesDeleted,
  onFileArchived,
  onFileRestored,
  multiSelect = true,
  category,
  status,
  className,
  refreshTrigger,
}: FileBrowserProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(initialFolderId);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());

  // Sync with initialFolderId prop when it changes (e.g. via URL query param)
  useEffect(() => {
    setCurrentFolderId(initialFolderId);
  }, [initialFolderId]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [breadcrumb, setBreadcrumb] = useState<FolderResponse[]>([]);
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState({ current: 0, total: 0, phase: '' });
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderDialogMode, setFolderDialogMode] = useState<'create' | 'rename'>('create');
  const [folderName, setFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState<FolderResponse | null>(null);
  const [isSubmittingFolder, setIsSubmittingFolder] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [filesToMove, setFilesToMove] = useState<string[]>([]);
  const [availableFolders, setAvailableFolders] = useState<FolderResponse[]>([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [isMovingFiles, setIsMovingFiles] = useState(false);
  const [deleteFolderDialog, setDeleteFolderDialog] = useState<{
    folder: FolderResponse;
    confirmText: string;
    isDeleting: boolean;
  } | null>(null);
  const [draggedFileIds, setDraggedFileIds] = useState<string[]>([]);
  const [dropTargetFolderId, setDropTargetFolderId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<UserFileResponse | null>(null);

  const {
    files,
    isLoading: filesLoading,
    filters,
    setFilters,
    updateFile,
    deleteFile,
    archiveFile,
    restoreFile,
    getDownloadUrl,
    bulkOperation,
    refresh,
  } = useFiles({
    category,
    status,
    // Pass null for root folder to filter for files with no folder
    // undefined means "don't filter by folder" which would show all files
    folderId: currentFolderId === undefined ? null : currentFolderId,
    search: searchQuery || undefined,
    autoFetch: true,
  });

  const {
    folders,
    isLoading: foldersLoading,
    getBreadcrumb,
    createFolder,
    renameFolder,
    deleteFolder,
    refresh: refreshFolders,
  } = useFolders({
    // Pass null for root to get only root-level folders
    // undefined means "don't filter" which returns all folders
    parentFolderId: currentFolderId === undefined ? null : currentFolderId,
    autoFetch: true,
  });

  const isLoading = filesLoading || foldersLoading;

  // Check if current folder is within My-Fias (can't create subfolders there)
  const currentFolderPath = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].path : null;
  const canCreateFolder = !isWithinMyFias(currentFolderPath);

  // Track if we've attempted to create the context folder
  const contextFolderCreatedRef = useRef(false);

  // Ensure My-Fias/Context folder exists on first visit
  // This effect runs after folders are loaded to avoid stale closure issues
  useEffect(() => {
    // Only run at root level and when folders have been loaded
    if (currentFolderId !== undefined || foldersLoading) return;
    if (contextFolderCreatedRef.current) return;

    // Check if My-Fias folder already exists
    const myFiasExists = folders.some((f) => f.path === '/My-Fias');
    if (myFiasExists) {
      contextFolderCreatedRef.current = true;
      return;
    }

    // Mark as attempted before making the API call
    contextFolderCreatedRef.current = true;

    api
      .ensureContextFolder()
      .then(() => {
        // Refresh folder list to show the new My-Fias folder
        refreshFolders();
      })
      .catch(() => {
        // Reset ref on error so we can retry
        contextFolderCreatedRef.current = false;
      });
  }, [currentFolderId, foldersLoading, folders, refreshFolders]);

  // Update breadcrumb when folder changes
  useEffect(() => {
    if (currentFolderId) {
      getBreadcrumb(currentFolderId).then(setBreadcrumb);
    } else {
      setBreadcrumb([]);
    }
  }, [currentFolderId, getBreadcrumb]);

  // Refresh files when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      refresh();
    }
  }, [refreshTrigger, refresh]);

  const handleFolderClick = useCallback((folderId: string) => {
    setCurrentFolderId(folderId);
    setSelectedFileIds(new Set());
  }, []);

  const handleBreadcrumbClick = useCallback((folderId: string | null) => {
    setCurrentFolderId(folderId || undefined);
    setSelectedFileIds(new Set());
  }, []);

  const handleFileClick = useCallback(
    (file: UserFileResponse, event: React.MouseEvent) => {
      const currentIndex = files.findIndex((f) => f.fileId === file.fileId);

      if (multiSelect && event.shiftKey && lastSelectedIndex !== null) {
        // Shift+click: select range
        const start = Math.min(lastSelectedIndex, currentIndex);
        const end = Math.max(lastSelectedIndex, currentIndex);
        const rangeFileIds = files.slice(start, end + 1).map((f) => f.fileId);
        setSelectedFileIds((prev) => {
          const next = new Set(prev);
          rangeFileIds.forEach((id) => next.add(id));
          return next;
        });
        setSelectionMode(true);
      } else if (multiSelect && (event.ctrlKey || event.metaKey || selectionMode)) {
        // Ctrl/Cmd+click or selection mode: toggle selection
        setSelectedFileIds((prev) => {
          const next = new Set(prev);
          if (next.has(file.fileId)) {
            next.delete(file.fileId);
          } else {
            next.add(file.fileId);
          }
          // Exit selection mode if nothing selected
          if (next.size === 0) {
            setSelectionMode(false);
          }
          return next;
        });
        setLastSelectedIndex(currentIndex);
      } else {
        // Normal click: select only this file
        setSelectedFileIds(new Set([file.fileId]));
        setLastSelectedIndex(currentIndex);
        onFileSelect?.(file);
      }
    },
    [multiSelect, onFileSelect, files, lastSelectedIndex, selectionMode],
  );

  const handleCheckboxChange = useCallback(
    (file: UserFileResponse, checked: boolean) => {
      setSelectedFileIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(file.fileId);
        } else {
          next.delete(file.fileId);
        }
        // Enter/exit selection mode based on selection count
        setSelectionMode(next.size > 0);
        return next;
      });
      const currentIndex = files.findIndex((f) => f.fileId === file.fileId);
      setLastSelectedIndex(currentIndex);
    },
    [files],
  );

  const handleLongPress = useCallback(
    (file: UserFileResponse) => {
      if (!multiSelect) return;
      // Enter selection mode and select this file
      setSelectionMode(true);
      setSelectedFileIds((prev) => {
        const next = new Set(prev);
        next.add(file.fileId);
        return next;
      });
      const currentIndex = files.findIndex((f) => f.fileId === file.fileId);
      setLastSelectedIndex(currentIndex);
    },
    [multiSelect, files],
  );

  const handleCancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedFileIds(new Set());
    setLastSelectedIndex(null);
  }, []);

  const handleFileDoubleClick = useCallback(
    (file: UserFileResponse) => {
      if (onFileOpen) {
        onFileOpen(file);
      } else {
        setPreviewFile(file);
      }
    },
    [onFileOpen],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedFileIds(new Set(files.map((f) => f.fileId)));
      } else {
        setSelectedFileIds(new Set());
      }
    },
    [files],
  );

  const handleDownload = useCallback(
    async (file: UserFileResponse) => {
      try {
        const url = await getDownloadUrl(file.fileId);
        // Fetch the file as a blob to catch errors
        const response = await fetch(url);
        if (!response.ok) {
          toast({
            title: 'Download failed',
            description: `File "${file.fileName}" could not be found. It may have been deleted from storage.`,
            variant: 'destructive',
          });
          return;
        }
        // Create a blob URL and trigger download
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast({
          title: 'Download failed',
          description: `Could not download "${file.fileName}". Please try again.`,
          variant: 'destructive',
        });
      }
    },
    [getDownloadUrl],
  );

  const handleDelete = useCallback(
    async (file: UserFileResponse) => {
      if (confirm(`Delete "${file.fileName}"?`)) {
        await deleteFile(file.fileId);
        setSelectedFileIds((prev) => {
          const next = new Set(prev);
          next.delete(file.fileId);
          return next;
        });
        onFilesDeleted?.([file.fileId]);
      }
    },
    [deleteFile, onFilesDeleted],
  );

  const handleArchive = useCallback(
    async (file: UserFileResponse) => {
      await archiveFile(file.fileId);
      setSelectedFileIds((prev) => {
        const next = new Set(prev);
        next.delete(file.fileId);
        if (next.size === 0) setSelectionMode(false);
        return next;
      });
      onFileArchived?.(file.fileId);
    },
    [archiveFile, onFileArchived],
  );

  const handleRestore = useCallback(
    async (file: UserFileResponse) => {
      await restoreFile(file.fileId);
      setSelectedFileIds((prev) => {
        const next = new Set(prev);
        next.delete(file.fileId);
        if (next.size === 0) setSelectionMode(false);
        return next;
      });
      onFileRestored?.(file.fileId);
    },
    [restoreFile, onFileRestored],
  );

  const handleBulkDelete = useCallback(async () => {
    const fileIds = Array.from(selectedFileIds);
    if (fileIds.length === 0) return;

    if (confirm(`Delete ${fileIds.length} file(s)?`)) {
      await bulkOperation('delete', fileIds);
      setSelectedFileIds(new Set());
      setSelectionMode(false);
      onFilesDeleted?.(fileIds);
    }
  }, [selectedFileIds, bulkOperation, onFilesDeleted]);

  const handleBulkArchive = useCallback(async () => {
    const fileIds = Array.from(selectedFileIds);
    if (fileIds.length === 0) return;

    for (const fileId of fileIds) {
      await archiveFile(fileId);
      onFileArchived?.(fileId);
    }
    setSelectedFileIds(new Set());
    setSelectionMode(false);
  }, [selectedFileIds, archiveFile, onFileArchived]);

  const handleBulkDownload = useCallback(async () => {
    const fileIds = Array.from(selectedFileIds);
    if (fileIds.length === 0) return;

    // Single file: download directly
    if (fileIds.length === 1) {
      const file = files.find((f) => f.fileId === fileIds[0]);
      if (file) {
        await handleDownload(file);
      }
      return;
    }

    // Multiple files: create a zip
    setIsCreatingZip(true);
    setZipProgress({ current: 0, total: fileIds.length, phase: 'Downloading' });
    const zip = new JSZip();
    const failedFiles: string[] = [];
    let successCount = 0;

    try {
      for (let i = 0; i < fileIds.length; i++) {
        const fileId = fileIds[i];
        const file = files.find((f) => f.fileId === fileId);
        const fileName = file?.fileName || 'file';
        setZipProgress({ current: i + 1, total: fileIds.length, phase: 'Downloading' });
        try {
          const url = await getDownloadUrl(fileId);
          const response = await fetch(url);
          if (!response.ok) {
            failedFiles.push(fileName);
            continue;
          }
          const blob = await response.blob();
          zip.file(fileName, blob);
          successCount++;
        } catch {
          failedFiles.push(fileName);
        }
      }

      if (successCount > 0) {
        // Generate and download the zip
        setZipProgress({ current: 0, total: 100, phase: 'Creating ZIP' });
        const zipBlob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
          setZipProgress({
            current: Math.round(metadata.percent),
            total: 100,
            phase: 'Creating ZIP',
          });
        });
        const blobUrl = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `files-${new Date().toISOString().slice(0, 10)}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);

        toast({
          title: 'Download complete',
          description: `Downloaded ${successCount} file(s) as a zip.`,
        });
      }

      if (failedFiles.length > 0) {
        toast({
          title: `${failedFiles.length} file(s) failed to download`,
          description:
            failedFiles.length <= 3
              ? `Could not download: ${failedFiles.join(', ')}`
              : `Could not download ${failedFiles.length} files. They may have been deleted from storage.`,
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Download failed',
        description: 'Failed to create zip file. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingZip(false);
      setZipProgress({ current: 0, total: 0, phase: '' });
    }
  }, [selectedFileIds, files, getDownloadUrl, handleDownload]);

  const handleSortChange = useCallback(
    (sortBy: SortBy) => {
      const newOrder: SortOrder =
        filters.sortBy === sortBy && filters.sortOrder === 'asc' ? 'desc' : 'asc';
      setFilters({ sortBy, sortOrder: newOrder });
    },
    [filters.sortBy, filters.sortOrder, setFilters],
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFilters({ search: searchQuery || undefined });
    },
    [searchQuery, setFilters],
  );

  const handleOpenCreateFolder = useCallback(() => {
    setFolderDialogMode('create');
    setFolderName('');
    setEditingFolder(null);
    setFolderDialogOpen(true);
  }, []);

  const handleOpenRenameFolder = useCallback((folder: FolderResponse) => {
    setFolderDialogMode('rename');
    setFolderName(folder.name);
    setEditingFolder(folder);
    setFolderDialogOpen(true);
  }, []);

  const handleFolderDialogSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!folderName.trim()) return;

      setIsSubmittingFolder(true);
      try {
        if (folderDialogMode === 'create') {
          await createFolder(folderName.trim(), currentFolderId);
          toast({
            title: 'Folder created',
            description: `Created folder "${folderName.trim()}"`,
          });
        } else if (editingFolder) {
          await renameFolder(editingFolder.folderId, folderName.trim());
          toast({
            title: 'Folder renamed',
            description: `Renamed to "${folderName.trim()}"`,
          });
        }
        setFolderDialogOpen(false);
        setFolderName('');
        setEditingFolder(null);
      } catch {
        toast({
          title:
            folderDialogMode === 'create' ? 'Failed to create folder' : 'Failed to rename folder',
          description: 'Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmittingFolder(false);
      }
    },
    [folderName, folderDialogMode, currentFolderId, editingFolder, createFolder, renameFolder],
  );

  const handleDeleteFolder = useCallback(
    async (folder: FolderResponse) => {
      // First try to delete empty folder
      try {
        await deleteFolder(folder.folderId, false);
        toast({
          title: 'Folder deleted',
          description: `Deleted folder "${folder.name}"`,
        });
        return;
      } catch (error) {
        // If folder is not empty, show confirmation dialog requiring user to type "delete"
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.includes('not empty') || errorMessage.includes('409')) {
          setDeleteFolderDialog({
            folder,
            confirmText: '',
            isDeleting: false,
          });
        } else {
          toast({
            title: 'Failed to delete folder',
            description: 'Please try again.',
            variant: 'destructive',
          });
        }
      }
    },
    [deleteFolder],
  );

  const handleConfirmDeleteFolder = useCallback(async () => {
    if (!deleteFolderDialog) return;

    setDeleteFolderDialog((prev) => (prev ? { ...prev, isDeleting: true } : null));

    try {
      await deleteFolder(deleteFolderDialog.folder.folderId, true);
      toast({
        title: 'Folder deleted',
        description: `Deleted folder "${deleteFolderDialog.folder.name}" and all its contents`,
      });
      setDeleteFolderDialog(null);
      // Refresh files list since files were deleted
      refresh();
    } catch {
      toast({
        title: 'Failed to delete folder',
        description: 'Please try again.',
        variant: 'destructive',
      });
      setDeleteFolderDialog((prev) => (prev ? { ...prev, isDeleting: false } : null));
    }
  }, [deleteFolderDialog, deleteFolder, refresh]);

  const handleOpenMoveDialog = useCallback(async (fileIds: string[]) => {
    setFilesToMove(fileIds);
    setMoveDialogOpen(true);
    setIsLoadingFolders(true);

    try {
      // Fetch all folders to show in the picker
      const response = await api.listFolders();
      setAvailableFolders(response.folders);
    } catch {
      toast({
        title: 'Failed to load folders',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingFolders(false);
    }
  }, []);

  const handleMoveFiles = useCallback(
    async (targetFolderId: string | null) => {
      if (filesToMove.length === 0) return;

      setIsMovingFiles(true);
      try {
        // Move files - use updateFile for single, bulkOperation for multiple
        for (const fileId of filesToMove) {
          await updateFile(fileId, { folderId: targetFolderId });
        }

        toast({
          title: 'Files moved',
          description: `Moved ${filesToMove.length} file(s) successfully.`,
        });

        setMoveDialogOpen(false);
        setFilesToMove([]);
        setSelectedFileIds(new Set());
        setSelectionMode(false);
        refresh();
      } catch (err) {
        console.error('Move files error:', err);
        toast({
          title: 'Failed to move files',
          description: err instanceof Error ? err.message : 'Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsMovingFiles(false);
      }
    },
    [filesToMove, updateFile, refresh],
  );

  const handleBulkMove = useCallback(() => {
    const fileIds = Array.from(selectedFileIds);
    if (fileIds.length > 0) {
      handleOpenMoveDialog(fileIds);
    }
  }, [selectedFileIds, handleOpenMoveDialog]);

  // Drag and drop handlers for moving files to folders
  const handleFileDragStart = useCallback(
    (fileId: string) => {
      // If the dragged file is selected, drag all selected files
      // Otherwise, drag just this file
      if (selectedFileIds.has(fileId) && selectedFileIds.size > 1) {
        setDraggedFileIds(Array.from(selectedFileIds));
      } else {
        setDraggedFileIds([fileId]);
      }
    },
    [selectedFileIds],
  );

  const handleFileDragEnd = useCallback(() => {
    setDraggedFileIds([]);
    setDropTargetFolderId(null);
  }, []);

  const handleFolderDragOver = useCallback(
    (e: React.DragEvent, folderId: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedFileIds.length > 0) {
        setDropTargetFolderId(folderId);
      }
    },
    [draggedFileIds],
  );

  const handleFolderDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetFolderId(null);
  }, []);

  const handleFolderDrop = useCallback(
    async (e: React.DragEvent, folderId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDropTargetFolderId(null);

      if (draggedFileIds.length === 0) return;

      const fileIdsToMove = [...draggedFileIds];
      setDraggedFileIds([]);

      try {
        for (const fileId of fileIdsToMove) {
          await updateFile(fileId, { folderId });
        }

        const folderName = folders.find((f) => f.folderId === folderId)?.name || 'folder';
        toast({
          title: 'Files moved',
          description: `Moved ${fileIdsToMove.length} file(s) to "${folderName}"`,
        });

        setSelectedFileIds(new Set());
        setSelectionMode(false);
      } catch (err) {
        toast({
          title: 'Failed to move files',
          description: err instanceof Error ? err.message : 'Please try again.',
          variant: 'destructive',
        });
      }
    },
    [draggedFileIds, updateFile, folders],
  );

  const selectedCount = selectedFileIds.size;
  const allSelected = files.length > 0 && selectedCount === files.length;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Selection Mode Banner */}
      {selectionMode && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-primary/10 border-b border-primary/20">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={handleCancelSelection}
            >
              <X className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium whitespace-nowrap">{selectedCount} selected</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelSelection}
              className="text-muted-foreground hidden sm:inline-flex"
            >
              Deselect All
            </Button>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {status !== 'archived' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDownload}
                  disabled={selectedCount === 0 || isCreatingZip}
                  title={selectedCount > 1 ? 'Download as ZIP' : 'Download'}
                  className="px-2 sm:px-3"
                >
                  {isCreatingZip ? (
                    <Loader2 className="h-4 w-4 sm:mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 sm:mr-1" />
                  )}
                  <span className="hidden sm:inline">
                    {isCreatingZip
                      ? zipProgress.phase === 'Downloading'
                        ? `${zipProgress.current}/${zipProgress.total} files...`
                        : `ZIP ${zipProgress.current}%`
                      : selectedCount > 1
                        ? 'Download ZIP'
                        : 'Download'}
                  </span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkMove}
                  disabled={selectedCount === 0}
                  title="Move to folder"
                  className="px-2 sm:px-3"
                >
                  <FolderInput className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Move</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkArchive}
                  disabled={selectedCount === 0}
                  title="Archive"
                  className="px-2 sm:px-3"
                >
                  <Archive className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Archive</span>
                </Button>
              </>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={selectedCount === 0}
              title="Delete"
              className="px-2 sm:px-3"
            >
              <Trash2 className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {!selectionMode && (
        <div className="flex items-center gap-2 sm:gap-4 p-4 border-b">
          {/* New Folder button - hidden when inside My-Fias */}
          {canCreateFolder && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenCreateFolder}
              className="flex-shrink-0"
            >
              <FolderPlus className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">New Folder</span>
            </Button>
          )}

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-sm">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search files..."
                className="pl-9"
              />
            </div>
          </form>

          {/* Sort */}
          <Select
            value={filters.sortBy}
            onValueChange={(value) => handleSortChange(value as SortBy)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="created_at">Date</SelectItem>
              <SelectItem value="size">Size</SelectItem>
            </SelectContent>
          </Select>

          {/* View toggle */}
          <div className="flex items-center gap-1 border rounded-md p-1">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 px-4 py-2 border-b text-sm">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          onClick={() => handleBreadcrumbClick(null)}
        >
          <Home className="h-4 w-4" />
        </Button>
        {breadcrumb.map((folder) => (
          <Fragment key={folder.folderId}>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => handleBreadcrumbClick(folder.folderId)}
            >
              {folder.name}
            </Button>
          </Fragment>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Select all (list view) */}
            {viewMode === 'list' && files.length > 0 && multiSelect && (
              <div className="flex items-center gap-3 mb-4 px-3">
                <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
                <span className="text-sm text-muted-foreground">Select all</span>
              </div>
            )}

            {/* Folders */}
            {folders.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Folders</h3>
                <div
                  className={cn(
                    viewMode === 'grid'
                      ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4'
                      : 'space-y-1',
                  )}
                >
                  {folders.map((folder) => (
                    <FolderItem
                      key={folder.folderId}
                      folder={folder}
                      viewMode={viewMode}
                      onClick={() => handleFolderClick(folder.folderId)}
                      onRename={() => handleOpenRenameFolder(folder)}
                      onDelete={() => handleDeleteFolder(folder)}
                      isDropTarget={dropTargetFolderId === folder.folderId}
                      isProtected={isProtectedFolder(folder.path)}
                      onDragOver={(e) => handleFolderDragOver(e, folder.folderId)}
                      onDragLeave={handleFolderDragLeave}
                      onDrop={(e) => handleFolderDrop(e, folder.folderId)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Files */}
            {files.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Files</h3>
                <div
                  className={cn(
                    viewMode === 'grid'
                      ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4'
                      : 'space-y-1',
                  )}
                >
                  {files.map((file) => (
                    <FileItem
                      key={file.fileId}
                      file={file}
                      viewMode={viewMode}
                      selected={selectedFileIds.has(file.fileId)}
                      multiSelect={multiSelect}
                      isArchived={file.status === 'archived'}
                      selectionMode={selectionMode}
                      isDragging={draggedFileIds.includes(file.fileId)}
                      onClick={(e) => handleFileClick(file, e)}
                      onDoubleClick={() => handleFileDoubleClick(file)}
                      onCheckboxChange={(checked) => handleCheckboxChange(file, checked)}
                      onLongPress={() => handleLongPress(file)}
                      onDownload={() => handleDownload(file)}
                      onMove={() => handleOpenMoveDialog([file.fileId])}
                      onDelete={() => handleDelete(file)}
                      onArchive={() => handleArchive(file)}
                      onRestore={() => handleRestore(file)}
                      onDragStart={() => handleFileDragStart(file.fileId)}
                      onDragEnd={handleFileDragEnd}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {folders.length === 0 && files.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <File className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No files yet</p>
                <p className="text-sm text-muted-foreground">Upload files to get started</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Folder Create/Rename Dialog */}
      <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
        <DialogContent>
          <form onSubmit={handleFolderDialogSubmit}>
            <DialogHeader>
              <DialogTitle>
                {folderDialogMode === 'create' ? 'Create New Folder' : 'Rename Folder'}
              </DialogTitle>
              <DialogDescription>
                {folderDialogMode === 'create'
                  ? 'Enter a name for the new folder.'
                  : 'Enter a new name for the folder.'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Folder name"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFolderDialogOpen(false)}
                disabled={isSubmittingFolder}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!folderName.trim() || isSubmittingFolder}>
                {isSubmittingFolder ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {folderDialogMode === 'create' ? 'Creating...' : 'Renaming...'}
                  </>
                ) : folderDialogMode === 'create' ? (
                  'Create'
                ) : (
                  'Rename'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Move Files Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move {filesToMove.length} file(s)</DialogTitle>
            <DialogDescription>Select a destination folder or move to the root.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 max-h-64 overflow-y-auto">
            {isLoadingFolders ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Root option */}
                <button
                  onClick={() => handleMoveFiles(null)}
                  disabled={isMovingFiles || currentFolderId === undefined}
                  className={cn(
                    'flex items-center gap-3 w-full px-3 py-2 rounded-lg transition-colors text-left',
                    currentFolderId === undefined
                      ? 'bg-muted text-muted-foreground cursor-not-allowed'
                      : 'hover:bg-accent',
                  )}
                >
                  <Home className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Root (My Data)</span>
                  {currentFolderId === undefined && (
                    <span className="text-xs text-muted-foreground ml-auto">(current)</span>
                  )}
                </button>

                {/* Folder list */}
                {availableFolders.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No folders available. Create a folder first.
                  </p>
                )}
                {availableFolders.map((folder) => {
                  const isCurrent = folder.folderId === currentFolderId;
                  return (
                    <button
                      key={folder.folderId}
                      onClick={() => handleMoveFiles(folder.folderId)}
                      disabled={isMovingFiles || isCurrent}
                      className={cn(
                        'flex items-start gap-3 w-full px-3 py-2 rounded-lg transition-colors text-left',
                        isCurrent
                          ? 'bg-muted text-muted-foreground cursor-not-allowed'
                          : 'hover:bg-accent',
                      )}
                    >
                      <Folder className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-medium break-words min-w-0">
                        {folder.path}
                        {isCurrent && (
                          <span className="text-xs text-muted-foreground ml-2">(current)</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMoveDialogOpen(false)}
              disabled={isMovingFiles}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <Dialog
        open={deleteFolderDialog !== null}
        onOpenChange={(open) => !open && setDeleteFolderDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete folder and all contents?</DialogTitle>
            <DialogDescription>
              Folder "{deleteFolderDialog?.folder.name}" contains files or subfolders. This action
              cannot be undone. All files and subfolders will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">
              Type <strong className="text-foreground">delete</strong> to confirm:
            </p>
            <Input
              value={deleteFolderDialog?.confirmText ?? ''}
              onChange={(e) =>
                setDeleteFolderDialog((prev) =>
                  prev ? { ...prev, confirmText: e.target.value } : null,
                )
              }
              placeholder="delete"
              autoFocus
              disabled={deleteFolderDialog?.isDeleting}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteFolderDialog(null)}
              disabled={deleteFolderDialog?.isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteFolder}
              disabled={
                deleteFolderDialog?.confirmText.toLowerCase() !== 'delete' ||
                deleteFolderDialog?.isDeleting
              }
            >
              {deleteFolderDialog?.isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Folder'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File preview modal */}
      {previewFile && <FilePreview file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
}

interface FolderItemProps {
  folder: FolderResponse;
  viewMode: ViewMode;
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
  isDropTarget?: boolean;
  isProtected?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

function FolderItem({
  folder,
  viewMode,
  onClick,
  onRename,
  onDelete,
  isDropTarget,
  isProtected = false,
  onDragOver,
  onDragLeave,
  onDrop,
}: FolderItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  // Prevent folder navigation while menu actions are processing
  const handleClick = useCallback(() => {
    if (!menuOpen) {
      onClick();
    }
  }, [menuOpen, onClick]);

  if (viewMode === 'grid') {
    return (
      <div
        onClick={handleClick}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'group relative flex flex-col items-center gap-2 p-4 rounded-lg border transition-colors cursor-pointer',
          isDropTarget ? 'border-primary bg-primary/10 ring-2 ring-primary' : 'hover:bg-accent',
        )}
      >
        <Folder className={cn('h-12 w-12', isDropTarget ? 'text-primary' : 'text-blue-500')} />
        <span className="text-sm font-medium truncate w-full text-center">{folder.name}</span>

        {/* Context menu - hidden for protected folders */}
        {!isProtected && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onRename}>
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onDelete} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'group flex items-center gap-3 w-full px-3 py-2 rounded-lg transition-colors cursor-pointer',
        isDropTarget ? 'bg-primary/10 ring-2 ring-primary' : 'hover:bg-accent',
      )}
    >
      <Folder
        className={cn('h-5 w-5 flex-shrink-0', isDropTarget ? 'text-primary' : 'text-blue-500')}
      />
      <span className="text-sm font-medium truncate flex-1">{folder.name}</span>

      {/* Context menu - hidden for protected folders */}
      {!isProtected && (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onRename}>
              <Pencil className="h-4 w-4 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} className="text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

interface FileItemProps {
  file: UserFileResponse;
  viewMode: ViewMode;
  selected: boolean;
  multiSelect: boolean;
  isArchived: boolean;
  selectionMode: boolean;
  isDragging?: boolean;
  onClick: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  onCheckboxChange: (checked: boolean) => void;
  onLongPress: () => void;
  onDownload: () => void;
  onMove: () => void;
  onDelete: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

function FileItem({
  file,
  viewMode,
  selected,
  multiSelect,
  isArchived,
  selectionMode,
  isDragging,
  onClick,
  onDoubleClick,
  onCheckboxChange,
  onLongPress,
  onDownload,
  onMove,
  onDelete,
  onArchive,
  onRestore,
  onDragStart,
  onDragEnd,
}: FileItemProps) {
  const FileIcon = getFileIcon(file.mimeType ?? undefined);
  const { thumbnailUrl, onThumbnailError } = useImageThumbnail(file);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressTriggered = useRef(false);

  const handlePressStart = useCallback(() => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      onLongPress();
    }, 500); // 500ms long press
  }, [onLongPress]);

  const handlePressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't trigger click if long-press was triggered
      if (longPressTriggered.current) {
        longPressTriggered.current = false;
        return;
      }
      onClick(e);
    },
    [onClick],
  );

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', file.fileId);
      onDragStart?.();
    },
    [file.fileId, onDragStart],
  );

  if (viewMode === 'grid') {
    return (
      <div
        draggable={!isArchived}
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handlePressEnd}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressEnd}
        className={cn(
          'group relative flex flex-col items-center gap-2 p-4 rounded-lg border cursor-pointer transition-colors',
          selected ? 'border-primary bg-primary/5' : 'hover:bg-accent',
          isDragging && 'opacity-50',
        )}
      >
        {/* Checkbox for multi-select */}
        {multiSelect && (
          <div
            className={cn(
              'absolute top-2 left-2 z-10 transition-opacity',
              selectionMode || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <Checkbox checked={selected} onCheckedChange={onCheckboxChange} />
          </div>
        )}

        {/* File icon or image thumbnail */}
        {thumbnailUrl ? (
          <div className="relative w-full aspect-square rounded overflow-hidden bg-muted flex items-center justify-center">
            <img
              src={thumbnailUrl}
              alt={file.fileName}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={onThumbnailError}
            />
            <Button
              variant="secondary"
              size="icon"
              className="absolute bottom-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-90 shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                onDoubleClick();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <Expand className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <FileIcon className="h-12 w-12 text-muted-foreground" />
        )}

        <span className="text-sm font-medium truncate w-full text-center">{file.fileName}</span>
        <span className="text-xs text-muted-foreground">{formatBytes(file.sizeBytes)}</span>

        {/* Context menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!isArchived && (
              <>
                <DropdownMenuItem onSelect={onDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onMove}>
                  <FolderInput className="h-4 w-4 mr-2" />
                  Move
                </DropdownMenuItem>
              </>
            )}
            {isArchived ? (
              <DropdownMenuItem onSelect={onRestore}>
                <ArchiveRestore className="h-4 w-4 mr-2" />
                Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={onArchive}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} className="text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div
      draggable={!isArchived}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      onDoubleClick={onDoubleClick}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      onTouchCancel={handlePressEnd}
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      className={cn(
        'group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors',
        selected ? 'bg-primary/10' : 'hover:bg-accent',
        isDragging && 'opacity-50',
      )}
    >
      {multiSelect && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <Checkbox checked={selected} onCheckedChange={onCheckboxChange} />
        </div>
      )}

      {thumbnailUrl ? (
        <div className="h-8 w-8 rounded overflow-hidden bg-muted flex-shrink-0">
          <img
            src={thumbnailUrl}
            alt={file.fileName}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={onThumbnailError}
          />
        </div>
      ) : (
        <FileIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.fileName}</p>
      </div>

      <span className="text-xs text-muted-foreground">{formatBytes(file.sizeBytes)}</span>

      <span className="text-xs text-muted-foreground w-24">
        {new Date(file.createdAt).toLocaleDateString()}
      </span>

      {thumbnailUrl && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 opacity-0 group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onDoubleClick();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <Expand className="h-4 w-4" />
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isArchived && (
            <>
              <DropdownMenuItem onSelect={onDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onMove}>
                <FolderInput className="h-4 w-4 mr-2" />
                Move
              </DropdownMenuItem>
            </>
          )}
          {isArchived ? (
            <DropdownMenuItem onSelect={onRestore}>
              <ArchiveRestore className="h-4 w-4 mr-2" />
              Restore
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={onArchive}>
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onDelete} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Cache for image thumbnail URLs (presigned URLs, keyed by fileId).
 * Shared across all FileItem instances to avoid redundant API calls.
 */
const thumbnailCache = new Map<string, string>();

function useImageThumbnail(file: UserFileResponse): {
  thumbnailUrl: string | null;
  onThumbnailError: () => void;
} {
  const isImage = file.mimeType?.startsWith('image/') ?? false;
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(
    () => thumbnailCache.get(file.fileId) ?? null,
  );
  const [failed, setFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!isImage || failed || thumbnailUrl) return;

    let cancelled = false;
    api.getDownloadUrl(file.fileId).then(
      (res) => {
        if (!cancelled) {
          thumbnailCache.set(file.fileId, res.downloadUrl);
          setThumbnailUrl(res.downloadUrl);
        }
      },
      () => {
        if (cancelled) return;
        // File not available in storage — show placeholder instead of removing
        setFailed(true);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [file.fileId, isImage, failed, thumbnailUrl, retryCount]);

  const onThumbnailError = useCallback(() => {
    // Expired presigned URL — clear cache and refetch (max 2 retries)
    thumbnailCache.delete(file.fileId);
    setThumbnailUrl(null);
    if (retryCount < 2) {
      setRetryCount((c) => c + 1);
    } else {
      setFailed(true);
    }
  }, [file.fileId, retryCount]);

  return { thumbnailUrl: isImage ? thumbnailUrl : null, onThumbnailError };
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return File;
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType.startsWith('video/')) return FileVideo;
  if (mimeType.startsWith('audio/')) return FileAudio;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text'))
    return FileText;
  return File;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default FileBrowser;
